import type { Localized, MessageKey } from "@/libs/localization/i18n";
import initialTasksData from "@/app/data/tasks.json";
import initialClientsData from "@/app/data/clients.json";
import paymentsData from "@/app/data/payments.json";
import budgetRowsData from "@/app/data/budget-rules.json";
import mailItemsData from "@/app/data/mail.json";
import debtRowsData from "@/app/data/debts.json";
import formItemsData from "@/app/data/forms.json";
import articlesData from "@/app/data/articles.json";

export const screens = [
  "work-queue",
  "dashboard",
  "clients",
  "communication",
  "bank",
  "mailroom",
  "invoicing",
  "court",
  "forms",
  "portal",
  "reports",
  "knowledge",
  "settings",
  "budget",
  "debts",
] as const;
export type Screen = (typeof screens)[number] | "client";
export const navigation: {
  screen: Screen;
  label: MessageKey;
  icon: string;
  group?: MessageKey;
  badge?: number;
}[] = [
  { screen: "work-queue", label: "workQueue", icon: "queue" },
  { screen: "dashboard", label: "dashboard", icon: "dashboard" },
  { screen: "clients", label: "clients", icon: "users", group: "clients" },
  { screen: "communication", label: "communication", icon: "chat", badge: 2 },
  { screen: "bank", label: "bank", icon: "bank", group: "moneyMail", badge: 6 },
  { screen: "mailroom", label: "mailroom", icon: "mail", badge: 12 },
  { screen: "invoicing", label: "invoicing", icon: "file" },
  { screen: "court", label: "court", icon: "court", group: "external" },
  { screen: "forms", label: "forms", icon: "form" },
  { screen: "portal", label: "portal", icon: "phone" },
  { screen: "reports", label: "reports", icon: "chart", group: "office" },
  { screen: "knowledge", label: "knowledge", icon: "book" },
  { screen: "settings", label: "settings", icon: "settings" },
];
export type Task = {
  id: string;
  category: "post" | "bank" | "portal" | "communication" | "court";
  priority: "high" | "medium" | "low";
  client: string;
  time: string;
  title: Localized;
  description: Localized;
  proposal: Localized;
  source: Localized;
  ai: boolean;
};
export type Client = {
  id: string;
  name: string;
  fullName: string;
  type: "guardianship" | "debtSupport" | "budgetManagement" | "mentoring";
  phase: "stable" | "intake" | "arrangement" | "stabilization";
  handler: string;
  signals: number;
};

function enumValue<const Values extends readonly string[]>(
  value: string,
  allowed: Values,
): Values[number] {
  if (!allowed.includes(value))
    throw new Error(`Invalid local demo value: ${value}`);
  return value as Values[number];
}
export const initialTasks: Task[] = initialTasksData.map((task) => ({
  ...task,
  category: enumValue(task.category, [
    "post",
    "bank",
    "portal",
    "communication",
    "court",
  ]),
  priority: enumValue(task.priority, ["high", "medium", "low"]),
}));
export const initialClients: Client[] = initialClientsData.map((client) => ({
  ...client,
  type: enumValue(client.type, [
    "guardianship",
    "debtSupport",
    "budgetManagement",
    "mentoring",
  ]),
  phase: enumValue(client.phase, [
    "stable",
    "intake",
    "arrangement",
    "stabilization",
  ]),
}));
export const payments = paymentsData;
export const budgetRows = budgetRowsData.map((row) => ({
  ...row,
  frequency: enumValue(row.frequency, ["monthly", "weekly"]),
}));
export const mailItems = mailItemsData;
export const debtRows = debtRowsData;
export const formItems = formItemsData;
export const articles = articlesData;
